import React, { useRef, useState, useEffect, useCallback } from 'react';

interface AggressivenessSliderProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

const AggressivenessSlider: React.FC<AggressivenessSliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 10,
  step = 0.1,
  disabled = false,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const calculateValueFromMouseEvent = useCallback((clientX: number) => {
    if (!sliderRef.current) return value;

    const { left, width } = sliderRef.current.getBoundingClientRect();
    const newPercentage = Math.max(0, Math.min(1, (clientX - left) / width));
    const newValue = min + newPercentage * (max - min);
    return parseFloat(newValue.toFixed(1));
  }, [min, max, value]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (disabled) return;
    setIsDragging(true);
    onChange(calculateValueFromMouseEvent(e.clientX));
    e.preventDefault();
  }, [disabled, onChange, calculateValueFromMouseEvent]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled) return;
    setIsDragging(true);
    onChange(calculateValueFromMouseEvent(e.touches[0].clientX));
    e.preventDefault();
  }, [disabled, onChange, calculateValueFromMouseEvent]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || disabled) return;
    onChange(calculateValueFromMouseEvent(e.clientX));
  }, [isDragging, disabled, onChange, calculateValueFromMouseEvent]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging || disabled) return;
    onChange(calculateValueFromMouseEvent(e.touches[0].clientX));
  }, [isDragging, disabled, onChange, calculateValueFromMouseEvent]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const percentage = ((value - min) / (max - min)) * 100;

  const getTrackGradient = () => {
    // Define color stops for a smoother transition
    const colorStops = [
      { value: 0, color: '#22C55E' }, // Green (Passive)
      { value: 3, color: '#22C55E' },
      { value: 4, color: '#EAB308' }, // Yellow (Balanced)
      { value: 6, color: '#EAB308' },
      { value: 7, color: '#EF4444' }, // Red (Assertive)
      { value: 10, color: '#EF4444' },
    ];

    let gradient = 'linear-gradient(to right, ';
    colorStops.forEach((stop, index) => {
      const stopPercentage = ((stop.value - min) / (max - min)) * 100;
      gradient += `${stop.color} ${stopPercentage}%`;
      if (index < colorStops.length - 1) {
        gradient += ', ';
      }
    });
    gradient += ')';
    return gradient;
  };

  const getThumbColor = (currentValue: number) => {
    if (currentValue <= 3) return 'bg-green-500';
    if (currentValue <= 6) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div 
      ref={sliderRef}
      className="relative w-full py-2 select-none"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Slider Track */}
      <div
        className="h-2 rounded-full cursor-pointer"
        style={{ background: getTrackGradient() }}
      ></div>

      {/* Range Input (fallback for keyboard navigation) */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="absolute top-0 left-0 w-full h-full opacity-0 pointer-events-none z-5"
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      />

      {/* Custom Thumb */}
      <div
        className={`absolute -top-1/2 transform -translate-x-1/2 w-10 h-10 rounded-full shadow-lg flex items-center justify-center text-xs font-bold text-white transition-all duration-200 z-20 ${
          getThumbColor(value)
        } ${
          isDragging ? 'scale-110 shadow-xl cursor-grabbing' : 'hover:scale-105 cursor-grab'
        } ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        style={{ left: `${percentage}%` }}
        onMouseDown={(e) => {
          e.stopPropagation();
          handleMouseDown(e);
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          handleTouchStart(e);
        }}
      >
        {value.toFixed(1)}
      </div>

      {/* Value Labels */}
      <div className="flex justify-between text-xs text-gray-500 mt-4">
        <span>0 (Passive)</span>
        <span>5 (Balanced)</span>
        <span>10 (Assertive)</span>
      </div>
    </div>
  );
};

export default AggressivenessSlider;
